import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Button } from './ui/button';
import { Menu, X, Moon, Sun } from 'lucide-react';

const Header = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [dark, setDark] = useState(() => document.documentElement.classList.contains('dark'));
  const location = useLocation();

  const toggleTheme = () => {
    const next = !dark;
    setDark(next);
    document.documentElement.classList.toggle('dark', next);
    localStorage.setItem('xsi-theme', next ? 'dark' : 'light');
  };

  const navLinks = [
    { path: '/', label: 'Who We Are' },
    { path: '/what-we-do', label: 'What We Do' },
    { path: '/engage', label: 'Engage With Us' },
    { path: '/partner', label: 'Partner With Us' },
    { path: '/connect', label: 'Connect' }
  ];

  const isActive = (path) => location.pathname === path;

  return (
    <header className="bg-white shadow-md sticky top-0 z-50">
      <div className="max-w-7xl mx-auto px-6">
        <div className="flex items-center justify-between py-3">
          {/* Logo */}
          <div className="flex items-center gap-4">
            <Link to="/" className="flex items-center gap-4" data-testid="header-logo-link">
              <img 
                src="https://customer-assets.emergentagent.com/job_xcelsior-web/artifacts/ye9jrchw_Screenshot_20251201-140847.jpg" 
                alt="XSI Logo" 
                className="h-20 lg:h-28 w-auto"
                data-testid="header-logo-image"
              />
            </Link>
            <div>
              <Link to="/" className="text-3xl lg:text-5xl font-bold text-gray-900 block" style={{ fontFamily: 'Montserrat, sans-serif' }}>
                XCELSIOR
              </Link>
              <Link to="/explore/our-story" className="text-sm lg:text-base text-[#00CED1] font-semibold hover:underline underline-offset-4" data-testid="header-tagline-link">
                Ascende Superius
              </Link>
            </div>
          </div>

          {/* Desktop Navigation */}
          <nav className="hidden lg:flex items-center gap-8">
            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                className={`font-semibold transition-all duration-300 hover:text-[#00CED1] ${
                  isActive(link.path)
                    ? 'text-[#00CED1] border-b-2 border-[#00CED1] pb-1'
                    : 'text-gray-700'
                }`}
              >
                {link.label}
              </Link>
            ))}
          </nav>

          {/* Desktop CTA */}
          <div className="hidden lg:flex items-center gap-3">
            <button
              onClick={toggleTheme}
              className="p-2 rounded-full text-gray-700 hover:text-[#00CED1] hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
              aria-label="Toggle theme"
              data-testid="theme-toggle-button"
            >
              {dark ? <Sun className="w-6 h-6" /> : <Moon className="w-6 h-6" />}
            </button>
            <Button 
              className="bg-[#FF8C00] hover:bg-[#e07d00] text-white font-semibold transition-all duration-300 transform hover:scale-105"
              onClick={() => window.location.href = '/engage'}
            >
              Get Started
            </Button>
          </div>

          {/* Mobile buttons */}
          <div className="lg:hidden flex items-center gap-1">
            <button
              onClick={toggleTheme}
              className="p-2 text-gray-700"
              aria-label="Toggle theme"
              data-testid="theme-toggle-button-mobile"
            >
              {dark ? <Sun className="w-6 h-6" /> : <Moon className="w-6 h-6" />}
            </button>
            <button
              className="p-2"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            >
              {mobileMenuOpen ? (
                <X className="w-6 h-6 text-gray-900" />
              ) : (
                <Menu className="w-6 h-6 text-gray-900" />
              )}
            </button>
          </div>
        </div>

        {/* Mobile Navigation */}
        {mobileMenuOpen && (
          <div className="lg:hidden py-4 border-t border-gray-200">
            <nav className="flex flex-col gap-4">
              {navLinks.map((link) => (
                <Link
                  key={link.path}
                  to={link.path}
                  className={`font-semibold py-2 transition-colors ${
                    isActive(link.path)
                      ? 'text-[#00CED1]'
                      : 'text-gray-700 hover:text-[#00CED1]'
                  }`}
                  onClick={() => setMobileMenuOpen(false)}
                >
                  {link.label}
                </Link>
              ))}
              <Button 
                className="bg-[#FF8C00] hover:bg-[#e07d00] text-white font-semibold mt-4"
                onClick={() => {
                  setMobileMenuOpen(false);
                  window.location.href = '/engage';
                }}
              >
                Get Started
              </Button>
            </nav>
          </div>
        )}
      </div>
    </header>
  );
};

export default Header;
